import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Button,
  Collapse,
} from '@mui/material';
import { Reply as ReplyIcon } from '@mui/icons-material';
import api from '../../services/api';

const OUTBOUND_DISABLED_MSG =
  'Manual comment replies from the inbox are not available yet. Auto-replies work for whitelisted accounts.';

const AUTO_REPLY_ACTIVE_MSG =
  'Auto-replies are active for whitelisted test accounts on incoming comments.';

const Comments = ({
  connectedAccounts = [],
  outboundEnabled = false,
  autoReplyEnabled = false,
  pageId: fixedPageId,
  embedded = false,
  allowReply = true,
}) => {
  const [pageId, setPageId] = useState(fixedPageId || '');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const canReply = allowReply && (outboundEnabled || autoReplyEnabled);
  const resolvePageId = () => fixedPageId || pageId || connectedAccounts[0]?.pageId || '';

  useEffect(() => {
    if (fixedPageId) {
      setPageId(fixedPageId);
    }
  }, [fixedPageId]);

  useEffect(() => {
    if (fixedPageId || connectedAccounts.length !== 1 || pageId) return;
    setPageId(connectedAccounts[0].pageId);
  }, [connectedAccounts, pageId, fixedPageId]);

  useEffect(() => {
    fetchComments();
    const interval = setInterval(fetchComments, 30000);
    return () => clearInterval(interval);
  }, [pageId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = pageId ? { page_id: pageId } : {};
      const response = await api.get('/lms/instagram/comments/', { params });
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Error fetching Instagram comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  };

  const openReply = (comment) => {
    setReplyingTo(comment.id);
    setReplyText('');
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const handleSendCommentReply = async (comment) => {
    if (!replyText.trim()) return;
    const currentPageId = resolvePageId();
    if (!currentPageId) {
      setError('Missing Facebook Page for reply.');
      return;
    }

    try {
      setSending(true);
      setError(null);
      await api.post('/lms/instagram/comments/reply/', {
        page_id: currentPageId,
        comment_id: comment.instagram_comment_id,
        message: replyText.trim(),
      });
      cancelReply();
      await fetchComments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send comment reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 2, height: embedded ? '520px' : 'auto', overflow: embedded ? 'auto' : 'visible' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!outboundEnabled && !embedded && (
        <Alert severity={autoReplyEnabled ? 'success' : 'info'} sx={{ mb: 2 }}>
          {autoReplyEnabled ? AUTO_REPLY_ACTIVE_MSG : OUTBOUND_DISABLED_MSG}
        </Alert>
      )}

      {connectedAccounts.length > 1 && !fixedPageId && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 220 }}>
          <InputLabel>Facebook Page</InputLabel>
          <Select value={pageId} label="Facebook Page" onChange={(e) => setPageId(e.target.value)}>
            <MenuItem value="">All Pages</MenuItem>
            {connectedAccounts.map((a) => (
              <MenuItem key={a.pageId} value={a.pageId}>
                {a.pageName} (@{a.instagramUsername})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Incoming comments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Comments arrive in real time when someone comments on your Instagram posts (Meta webhook).
        </Typography>

        {loading && comments.length === 0 ? (
          <CircularProgress size={24} />
        ) : comments.length === 0 ? (
          <Typography color="text.secondary">
            No comments yet. Ensure Meta webhooks are subscribed for instagram → comments.
          </Typography>
        ) : (
          <List disablePadding>
            {comments.map((c) => (
              <ListItem
                key={c.id}
                alignItems="flex-start"
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    @{c.username || c.user_id || 'unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(c.comment_timestamp || c.created_at)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {c.text || '(no text)'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {c.parent_comment_id && <Chip size="small" label="Reply" variant="outlined" />}
                  {c.instagram_username && (
                    <Chip size="small" label={`via @${c.instagram_username}`} variant="outlined" />
                  )}
                  {c.instagram_media_id && (
                    <Chip size="small" label={`Post ${c.instagram_media_id}`} variant="outlined" />
                  )}
                  {canReply && !c.parent_comment_id && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openReply(c)}
                      sx={{
                        ml: 'auto',
                        textTransform: 'none',
                        borderColor: '#E4405F',
                        color: '#E4405F',
                        '&:hover': { borderColor: '#c13584', bgcolor: 'rgba(228, 64, 95, 0.04)' },
                      }}
                    >
                      Reply
                    </Button>
                  )}
                </Box>

                <Collapse in={replyingTo === c.id}>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={3}
                      placeholder="Write a reply…"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={sending}
                    />
                    <Button
                      variant="contained"
                      disabled={sending || !replyText.trim()}
                      onClick={() => handleSendCommentReply(c)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#E4405F',
                        '&:hover': { bgcolor: '#c13584' },
                      }}
                    >
                      Send
                    </Button>
                    <Button variant="text" onClick={cancelReply} disabled={sending} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                  </Box>
                </Collapse>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Comments;
